import { useState } from "react";
import type { PickSpec } from "@howcomet/shared";
import { CheckFrame } from "./CheckFrame";
import type { ShellProps } from "./registry";

export function PickShell({ spec, onDone, collecting }: ShellProps<PickSpec>) {
  /*
    Rendered in the order given, and that is not the oversight it looks like.

    `pick` options arrive shuffled — the server does it in `servableSpec`, once
    per attempt, and stores what it sent. Unlike `order` and `match`, whose specs
    carry the TRUE order and so can only be hidden here, `pick` marks its answer
    with `correct: true` and the order carries nothing. Hiding it on the server
    means the fix reaches iPads that are already installed: the web bundle ships
    inside the binary, so anything done here waits for an App Store release.
  */
  const options = spec.options;

  const [picked, setPicked] = useState<number | null>(null);
  const [misses, setMisses] = useState(0);

  const chosen = picked === null ? null : options[picked];
  const outcome = chosen ? (chosen.correct ? "right" : "wrong") : null;

  return (
    <CheckFrame
      spec={spec}
      outcome={outcome}
      onRetry={() => setPicked(null)}
      onCollect={() => onDone({ correct: true, firstTry: misses === 0 })}
      collecting={collecting}
    >
      <div className="flex flex-col gap-3">
        {options.map((option, index) => {
          const isChosen = picked === index;
          const locked = outcome === "right";
          return (
            <button
              key={option.label}
              disabled={locked}
              onClick={() => {
                setPicked(index);
                if (!option.correct) setMisses((m) => m + 1);
              }}
              aria-pressed={isChosen}
              className={`kidbtn disp flex items-center gap-3 rounded-2xl px-5 py-4 text-left text-[20px] ${
                isChosen
                  ? option.correct
                    ? "bg-mint text-night"
                    : "bg-amber text-night"
                  : "bg-panel text-cream"
              } ${locked && !isChosen ? "opacity-45" : ""}`}
            >
              {option.emoji && <span className="text-[26px]">{option.emoji}</span>}
              {option.label}
            </button>
          );
        })}
      </div>
    </CheckFrame>
  );
}
